/**
 * Motor de alertas (§13.1): traduce eventos de dominio en alertas (borradores) y las materializa
 * aplicando catálogo, preferencias, deduplicación, horario silencioso, tope diario y plantillas.
 * Todo es puro: la fachada persiste el resultado en la misma transacción del cambio (outbox).
 */
import {
  AlertaBorrador, AlertaEntity, CodigoAlerta, CorreoSalidaEntity, DestinatarioAlerta, PlantillaCorreoEntity,
  PreferenciaNotificacionEntity, TipoAlertaDef
} from '../models/alertas.model';
import { BanderasAsistencia, DatosCiclo, ParticipanteEntity, SesionEntity, UsuarioEntity } from '../models/asistencia.model';
import { EventoAuditoria } from '../models/auditoria.model';
import { definicionAlerta } from '../data/catalogo-alertas';
import { cubiertoPorEquivalencia, esAsistenciaVigente, sonSesionesEquivalentes, ventanaToma } from './asistencia-rules';
import { documentoEnmascarado, indice, mentorDeSesion, mentorTitularDeGrupo, nombreCompleto, nombreCorto } from './indice';
import { renderizarPlantilla } from './plantillas';
import { correoValido } from './participante-rules';
import {
  enHorarioSilencioso, fechaBogota, finSesion, formatoFechaCorta, formatoFechaLarga, formatoFranja, formatoHora12,
  formatoInstante, proximaHora
} from './tiempo';

// ----------------------------------------------------------------------------------------------
// Destinatarios y variables comunes
// ----------------------------------------------------------------------------------------------
export function destinoEstudiante(p: ParticipanteEntity): DestinatarioAlerta {
  return { id: p.id, tipo: 'PARTICIPANTE', rol: 'ESTUDIANTE_MML', nombre: nombreCompleto(p), correo: p.correo };
}

export function destinoUsuario(u: UsuarioEntity): DestinatarioAlerta {
  return { id: u.id, tipo: 'USUARIO', rol: u.rol, nombre: u.nombre, correo: u.correo };
}

export function coordinadores(datos: DatosCiclo): DestinatarioAlerta[] {
  return datos.usuarios.filter(u => u.activo && u.rol === 'COORDINADOR_MML').map(destinoUsuario);
}

export function varsSesion(datos: DatosCiclo, s: SesionEntity): Record<string, string> {
  const idx = indice(datos);
  const g = idx.grupos.get(s.grupoId);
  return {
    grupo: g?.codigo ?? '',
    modulo: idx.modulos.get(s.moduloId)?.nombre ?? '',
    fecha: formatoFechaLarga(s.fecha),
    fechaCorta: formatoFechaCorta(s.fecha),
    horario: formatoFranja(s.horaInicio, s.horaFin),
    hora: formatoHora12(s.horaInicio),
    clase: `Clase ${s.numero}`,
    mentor: mentorDeSesion(datos, s)?.nombre ?? 'por asignar'
  };
}

const RUTA_PORTAL = '/mi-progreso-mml';
const rutaSesion = (id: string) => `/admin-asistencia-mml/sesiones/${id}`;
const rutaParticipante = (id: string) => `/admin-asistencia-mml/participantes/${id}`;

function primerNombre(d: DestinatarioAlerta): string {
  return d.nombre.split(/\s+/)[0] ?? d.nombre;
}

function b(
  tipo: CodigoAlerta, destinatario: DestinatarioAlerta, vars: Record<string, string>, claveEntidad: string, ruta: string,
  contexto: AlertaBorrador['contexto'], correosAdicionales?: string[]
): AlertaBorrador {
  return { tipo, destinatario, vars: { nombre: primerNombre(destinatario), ...vars }, claveEntidad, ruta, contexto, correosAdicionales };
}

function cambio(ev: EventoAuditoria, campo: string, sujetoId?: string): { antes: unknown; despues: unknown } | undefined {
  return ev.cambios.find(c => c.campo === campo && (sujetoId === undefined || c.sujetoId === sujetoId));
}

const ESTADO_LABEL: Record<string, string> = { PRESENTE: 'Presente', AUSENTE: 'Ausente', EXCUSA: 'Excusa', ANULADA: 'Anulada' };

/** Otras clases equivalentes (otros grupos del rol) que aún no terminan: recuperación (RF-54). */
export function opcionesRecuperacion(datos: DatosCiclo, participanteId: string, moduloId: string, ahora: Date, sesionReferencia?: SesionEntity): SesionEntity[] {
  const idx = indice(datos);
  const insc = idx.inscripcionActiva.get(participanteId);
  const rolId = insc ? idx.grupos.get(insc.grupoId)?.rolId : undefined;
  return datos.sesiones
    .filter(s => {
      if (s.eliminado || s.estado === 'CANCELADA' || s.grupoId === insc?.grupoId) return false;
      if (finSesion(s).getTime() <= ahora.getTime()) return false;
      if (sesionReferencia) return sonSesionesEquivalentes(datos, sesionReferencia, s);
      return s.moduloId === moduloId && idx.grupos.get(s.grupoId)?.rolId === rolId;
    })
    .sort((a, c) => (a.fecha + a.horaInicio).localeCompare(c.fecha + c.horaInicio))
    .slice(0, 3);
}

// ----------------------------------------------------------------------------------------------
// Alertas por evento (RF-56)
// ----------------------------------------------------------------------------------------------
export function alertasPorEvento(ev: EventoAuditoria, datos: DatosCiclo, ahora: Date): AlertaBorrador[] {
  const idx = indice(datos);
  const out: AlertaBorrador[] = [];
  const ctx = ev.contexto;
  const sesion = ctx.sesionId ? idx.sesiones.get(ctx.sesionId) : undefined;
  const participante = ctx.participanteId ? idx.participantes.get(ctx.participanteId) : undefined;
  const vS = sesion ? varsSesion(datos, sesion) : {};

  switch (ev.accion) {
    case 'SESION_TOMADA': {
      if (!sesion) break;
      for (const c of ev.cambios.filter(x => x.campo === 'estado' && x.despues === 'AUSENTE' && x.antes !== 'AUSENTE' && x.sujetoId)) {
        const p = idx.participantes.get(c.sujetoId as string);
        if (!p) continue;
        // Si la clase ya está cubierta por asistencia en otro grupo, la inasistencia en su grupo está conciliada y no dispara alerta
        if (cubiertoPorEquivalencia(datos, sesion, p.id)) continue;
        const previas = (idx.asistenciasPorParticipante.get(p.id) ?? [])
          .filter(a => {
            if (!esAsistenciaVigente(a) || a.estado !== 'AUSENTE' || a.sesionId === sesion.id) return false;
            const sAnt = idx.sesiones.get(a.sesionId);
            return !sAnt || !cubiertoPorEquivalencia(datos, sAnt, p.id);
          }).length;
        if (previas > 0) continue; // A2: solo la primera inasistencia no conciliada
        const opciones = opcionesRecuperacion(datos, p.id, sesion.moduloId, ahora, sesion)
          .map(s => `${idx.grupos.get(s.grupoId)?.codigo} ${formatoFechaCorta(s.fecha)} ${formatoFranja(s.horaInicio, s.horaFin)}`);
        out.push(b('EST-04', destinoEstudiante(p), { ...vS, opciones: opciones.join('; ') || 'consulta con tu mentor' }, sesion.id, RUTA_PORTAL,
          { cicloId: datos.ciclo.id, sesionId: sesion.id, participanteId: p.id, grupoId: sesion.grupoId }));
      }
      break;
    }
    case 'ASISTENCIA_EXTRA_GRUPO_REGISTRADA': {
      if (!sesion || !participante) break;
      const grupoOrigenId = String(cambio(ev, 'grupoOrigenId')?.despues ?? '');
      const cuenta = cambio(ev, 'cuentaParaModuloId')?.despues;
      const yaCumplido = cambio(ev, 'moduloYaCumplido')?.despues === true;
      const concilia = cambio(ev, 'conciliaInasistencia')?.despues === true;
      const vars = {
        ...vS,
        participante: nombreCorto(nombreCompleto(participante)),
        grupoOrigen: idx.grupos.get(grupoOrigenId)?.codigo ?? '',
        grupoSesion: idx.grupos.get(sesion.grupoId)?.codigo ?? '',
        mentorRegistra: ev.actor.nombre,
        efecto: yaCumplido
          ? 'El módulo ya estaba cumplido: esta asistencia queda registrada pero no suma de nuevo.'
          : concilia
            ? 'Esta asistencia concilia la inasistencia en tu grupo titular y cuenta por equivalencia para el módulo.'
            : cuenta
              ? 'Esta asistencia cuenta por equivalencia para el módulo.'
              : 'Esta asistencia quedó registrada pero no suma al cumplimiento.'
      };
      const contexto = { cicloId: datos.ciclo.id, sesionId: sesion.id, participanteId: participante.id, grupoId: grupoOrigenId };
      const titular = mentorTitularDeGrupo(datos, grupoOrigenId);
      if (titular && titular.id !== ev.actor.id) out.push(b('MEN-04', destinoUsuario(titular), vars, `${sesion.id}|${participante.id}`, rutaParticipante(participante.id), contexto));
      out.push(b('EST-07', destinoEstudiante(participante), vars, sesion.id, RUTA_PORTAL, contexto));
      if (cambio(ev, 'aforoSuperado')?.despues === true) {
        const g = idx.grupos.get(sesion.grupoId);
        const presentes = (idx.asistenciasPorSesion.get(sesion.id) ?? []).filter(a => esAsistenciaVigente(a) && a.estado === 'PRESENTE').length + 1;
        for (const c of coordinadores(datos)) {
          out.push(b('COO-08', c, { ...vS, presentes: String(presentes), cupo: String(g?.cupo ?? '') }, sesion.id, rutaSesion(sesion.id), { cicloId: datos.ciclo.id, sesionId: sesion.id }));
        }
      }
      break;
    }
    case 'ASISTENCIA_EXTRA_GRUPO_BLOQUEADA': {
      if (!sesion || !participante || ev.codigoError !== 'ASIS-004') break;
      const vars = {
        ...vS,
        participante: `${nombreCorto(nombreCompleto(participante))} (${documentoEnmascarado(participante.numeroDocumento)})`,
        mentorRegistra: ev.actor.nombre,
        grupoSesion: idx.grupos.get(sesion.grupoId)?.codigo ?? '',
        grupoChoque: String(cambio(ev, 'grupoChoque')?.despues ?? '')
      };
      for (const c of coordinadores(datos)) {
        out.push(b('COO-03', c, vars, `${sesion.id}|${participante.id}`, rutaParticipante(participante.id), { cicloId: datos.ciclo.id, sesionId: sesion.id, participanteId: participante.id }));
      }
      break;
    }
    case 'ASISTENCIA_CORREGIDA':
    case 'ASISTENCIA_ANULADA': {
      if (!sesion || !participante) break;
      const c = cambio(ev, ev.accion === 'ASISTENCIA_ANULADA' ? 'validacion' : 'estado');
      const antes = ESTADO_LABEL[String(c?.antes ?? '')] ?? 'Sin registro';
      const despues = ev.accion === 'ASISTENCIA_ANULADA' ? 'Anulada' : ESTADO_LABEL[String(c?.despues ?? '')] ?? String(c?.despues ?? '');
      out.push(b('EST-08', destinoEstudiante(participante), { ...vS, antes, despues, motivo: ev.motivo ?? '—' }, `${ev.id}`, RUTA_PORTAL,
        { cicloId: datos.ciclo.id, sesionId: sesion.id, participanteId: participante.id }));
      break;
    }
    case 'SESION_CANCELADA':
    case 'SESION_REPROGRAMADA': {
      if (!sesion) break;
      const novedad = ev.accion === 'SESION_CANCELADA' ? 'cancelada' : 'reprogramada';
      const nuevaFecha = cambio(ev, 'fecha')?.despues;
      const detalle = ev.accion === 'SESION_REPROGRAMADA'
        ? `Nueva fecha: ${formatoFechaLarga(String(nuevaFecha ?? sesion.fecha))} ${String(cambio(ev, 'horaInicio')?.despues ?? sesion.horaInicio)}.`
        : 'Revisa en la plataforma las opciones para recuperar el módulo.';
      const vars = { ...vS, fecha: formatoFechaLarga(String(cambio(ev, 'fecha')?.antes ?? sesion.fecha)), novedad, detalle, motivo: ev.motivo ?? '—' };
      for (const i of idx.inscritosActivosPorGrupo.get(sesion.grupoId) ?? []) {
        const p = idx.participantes.get(i.participanteId);
        if (p) out.push(b('EST-10', destinoEstudiante(p), vars, `${sesion.id}|${ev.id}`, RUTA_PORTAL, { cicloId: datos.ciclo.id, sesionId: sesion.id, participanteId: p.id }));
      }
      const mentor = mentorDeSesion(datos, sesion);
      if (mentor) out.push(b('MEN-08', destinoUsuario(mentor), vars, `${sesion.id}|${ev.id}`, rutaSesion(sesion.id), { cicloId: datos.ciclo.id, sesionId: sesion.id }));
      break;
    }
    case 'MENTOR_TITULAR_ASIGNADO':
    case 'MENTOR_SUPLENTE_ASIGNADO': {
      const nuevo = idx.usuarios.get(String(cambio(ev, ev.accion === 'MENTOR_TITULAR_ASIGNADO' ? 'mentorTitularId' : 'mentorAsignadoId')?.despues ?? ''));
      if (!nuevo) break;
      const g = idx.grupos.get(ctx.grupoId ?? sesion?.grupoId ?? '');
      const titular = ev.accion === 'MENTOR_TITULAR_ASIGNADO';
      out.push(b('MEN-06', destinoUsuario(nuevo), {
        grupo: g?.codigo ?? '',
        tipoAsignacion: titular ? 'mentor titular' : 'mentor suplente',
        alcance: titular ? `el grupo ${g?.codigo}` : `la sesión ${vS['clase'] ?? ''} del grupo ${g?.codigo} (${vS['fecha'] ?? ''}, ${vS['horario'] ?? ''})`
      }, ev.id, titular ? '/admin-asistencia-mml/sesiones' : rutaSesion(sesion?.id ?? ''), { cicloId: datos.ciclo.id, grupoId: g?.id, sesionId: sesion?.id }));
      break;
    }
    case 'INSCRIPCION_CREADA':
    case 'INSCRIPCION_CAMBIO_GRUPO': {
      if (!participante) break;
      const grupoId = String(cambio(ev, 'grupoId')?.despues ?? ctx.grupoId ?? '');
      const g = idx.grupos.get(grupoId);
      if (!g) break;
      const sesiones = idx.sesionesPorGrupo.get(g.id) ?? [];
      const primera = sesiones[0];
      const vars = {
        grupo: g.codigo,
        grupoAnterior: idx.grupos.get(String(cambio(ev, 'grupoId')?.antes ?? ''))?.codigo ?? '',
        nivel: g.nivel === 'INICIACION' ? 'Iniciación' : g.nivel === 'AVANZADO' ? 'Avanzado' : 'Único',
        rol: idx.roles.get(g.rolId)?.nombre ?? '',
        horario: `${g.diasLabel} ${g.franjaLabel}`,
        primeraClase: primera ? `${formatoFechaLarga(primera.fecha)} ${formatoHora12(primera.horaInicio)}` : 'por confirmar',
        comprobante: ev.entidadId.toUpperCase().slice(-8),
        avisoCuenta: participante.cuentaMJ.estado === 'REGISTRADO'
          ? 'Puedes seguir tu progreso en Medellín Joven con tu cuenta.'
          : 'Para ver tu progreso, crea tu cuenta en Medellín Joven con este mismo correo.'
      };
      out.push(b(ev.accion === 'INSCRIPCION_CREADA' ? 'EST-01' : 'EST-02', destinoEstudiante(participante), vars, ev.entidadId, RUTA_PORTAL,
        { cicloId: datos.ciclo.id, participanteId: participante.id, grupoId: g.id }));
      const inscritos = (idx.inscritosActivosPorGrupo.get(g.id) ?? []).length + 1;
      const ocupacion = inscritos / g.cupo;
      if (ocupacion >= 0.9) {
        const umbral = ocupacion >= 1 ? '100' : '90';
        for (const c of coordinadores(datos)) {
          out.push(b('COO-05', c, { grupo: g.codigo, ocupacion: `${Math.round(ocupacion * 100)} %`, inscritos: String(inscritos), cupo: String(g.cupo) },
            `${g.id}|${umbral}`, '/admin-asistencia-mml/grupos', { cicloId: datos.ciclo.id, grupoId: g.id }));
        }
      }
      break;
    }
    case 'PARTICIPANTE_EDITADO':
    case 'SOLICITUD_APROBADA': {
      if (ev.accion === 'SOLICITUD_APROBADA') out.push(...alertaSolicitud(ev, datos));
      if (!participante) break;
      const correo = cambio(ev, 'correo');
      const doc = cambio(ev, 'numeroDocumento');
      if (!correo && !doc) break;
      const campos = ev.cambios.map(c => ({ correo: 'correo', numeroDocumento: 'documento', tipoDocumento: 'tipo de documento', nombres: 'nombres', apellidos: 'apellidos', celular: 'celular' } as Record<string, string>)[c.campo]).filter(Boolean);
      const destino = { ...destinoEstudiante(participante), correo: String(correo?.despues ?? participante.correo) };
      out.push(b('EST-12', destino, { fecha: formatoInstante(ev.timestamp), campos: campos.join(', ') }, ev.id, RUTA_PORTAL,
        { cicloId: datos.ciclo.id, participanteId: participante.id }, correo ? [String(correo.antes)] : undefined));
      break;
    }
    case 'SOLICITUD_RECHAZADA':
      out.push(...alertaSolicitud(ev, datos));
      break;
    case 'CUENTA_MJ_VERIFICADA': {
      for (const c of ev.cambios.filter(x => x.campo === 'cuentaMJ.estado' && x.sujetoId)) {
        const p = idx.participantes.get(c.sujetoId as string);
        if (!p) continue;
        if (c.despues === 'NO_REGISTRADO') {
          out.push(b('EST-09', destinoEstudiante(p), { correo: p.correo }, `${p.id}|${p.correo}`, '/iniciar-sesion', { cicloId: datos.ciclo.id, participanteId: p.id }));
        }
      }
      const errores = ev.cambios.filter(x => x.campo === 'cuentaMJ.estado' && x.despues === 'ERROR_VERIFICACION').length;
      if (errores) {
        for (const c of coordinadores(datos)) {
          out.push(b('COO-10', c, { detalle: `El servicio de Medellín Joven no respondió para ${errores} verificación(es) (MJ-001)` }, ev.id, '/admin-asistencia-mml/participantes?cuentaMJ=ERROR_VERIFICACION', { cicloId: datos.ciclo.id }));
        }
      }
      break;
    }
    case 'EXCUSA_APROBADA':
    case 'EXCUSA_RECHAZADA': {
      if (!participante || !sesion) break;
      const aprobada = ev.accion === 'EXCUSA_APROBADA';
      out.push(b('EST-13', destinoEstudiante(participante), {
        ...vS, resultado: aprobada ? 'aprobada' : 'rechazada',
        detalle: aprobada ? 'La clase no cuenta en tu porcentaje de asistencia.' : `Motivo: ${ev.motivo ?? '—'}. Puedes recuperar el módulo en otra clase.`
      }, ev.id, RUTA_PORTAL, { cicloId: datos.ciclo.id, sesionId: sesion.id, participanteId: participante.id }));
      break;
    }
    case 'INTEGRIDAD_VERIFICADA': {
      if (cambio(ev, 'integra')?.despues !== false) break;
      for (const c of coordinadores(datos)) {
        out.push(b('COO-11', c, { secuencia: String(cambio(ev, 'secuenciaRota')?.despues ?? '?'), detalle: String(cambio(ev, 'detalle')?.despues ?? '') },
          `${cambio(ev, 'secuenciaRota')?.despues}`, '/admin-asistencia-mml/trazabilidad', { cicloId: datos.ciclo.id }));
      }
      break;
    }
    case 'INSCRIPCION_INTENTO_FALLIDO': {
      const n = Number(cambio(ev, 'intentosRecientes')?.despues ?? 0);
      if (n < 5) break;
      const doc = String(cambio(ev, 'documento')?.despues ?? '');
      const huella = String(cambio(ev, 'documentoHuella')?.despues ?? doc);
      const ventana = Math.floor(ahora.getTime() / 600_000);
      for (const c of coordinadores(datos)) {
        out.push(b('COO-12', c, { cantidad: String(n), documento: doc ? documentoEnmascarado(doc) : '(lista de nombres)' }, `${huella}|${ventana}`, '/admin-asistencia-mml/trazabilidad?accion=INSCRIPCION_INTENTO_FALLIDO', { cicloId: datos.ciclo.id }));
      }
      break;
    }
    case 'IMPORTACION_EJECUTADA':
    case 'CRONOGRAMA_IMPORTADO': {
      const errores = Number(cambio(ev, 'errores')?.despues ?? 0);
      if (!errores) break;
      for (const c of coordinadores(datos)) {
        out.push(b('COO-13', c, { errores: String(errores), procesadas: String(cambio(ev, 'procesadas')?.despues ?? 0), tipo: ev.accion === 'CRONOGRAMA_IMPORTADO' ? 'Cronograma' : 'Habilitados' },
          ev.id, `/admin-asistencia-mml/trazabilidad`, { cicloId: datos.ciclo.id }));
      }
      break;
    }
    case 'DESPACHO_CORREOS_EJECUTADO': {
      for (const c of ev.cambios.filter(x => x.campo === 'fallido' && x.sujetoId)) {
        for (const co of coordinadores(datos)) {
          out.push(b('COO-10', co, { detalle: `El correo a ${String(c.despues)} falló tras 3 reintentos` }, String(c.sujetoId), '/admin-asistencia-mml/correos', { cicloId: datos.ciclo.id, correoId: String(c.sujetoId) }));
        }
      }
      break;
    }
    default:
      break;
  }
  return out;
}

function alertaSolicitud(ev: EventoAuditoria, datos: DatosCiclo): AlertaBorrador[] {
  const solicitante = indice(datos).usuarios.get(String(cambio(ev, 'solicitadaPor')?.despues ?? ''));
  if (!solicitante) return [];
  return [b('MEN-07', destinoUsuario(solicitante), {
    descripcion: String(cambio(ev, 'descripcion')?.despues ?? ev.resumen),
    resultado: ev.accion === 'SOLICITUD_APROBADA' ? 'aprobada' : 'rechazada',
    motivo: ev.motivo ?? '—'
  }, ev.entidadId, '/admin-asistencia-mml/solicitudes', { cicloId: datos.ciclo.id, solicitudId: ev.entidadId })];
}

/** Alertas que un evento deja sin vigencia (auto-resolución inmediata, RF-57). */
export function resolucionesPorEvento(ev: EventoAuditoria): { tipos: CodigoAlerta[]; sesionId?: string; grupoId?: string } | null {
  switch (ev.accion) {
    case 'SESION_TOMADA':
      return { tipos: ['MEN-02', 'MEN-03', 'COO-01'], sesionId: ev.contexto.sesionId };
    case 'SESION_CANCELADA':
      return { tipos: ['MEN-02', 'MEN-03', 'COO-01', 'COO-09', 'EST-03'], sesionId: ev.contexto.sesionId };
    case 'EVIDENCIA_CARGADA':
      return { tipos: ['MEN-09'], sesionId: ev.contexto.sesionId };
    case 'MENTOR_SUPLENTE_ASIGNADO':
      return { tipos: ['COO-09'], sesionId: ev.contexto.sesionId };
    default:
      return null;
  }
}

// ----------------------------------------------------------------------------------------------
// Materialización (catálogo + preferencias + anti-fatiga + plantillas)
// ----------------------------------------------------------------------------------------------
export interface ContextoMaterializacion {
  catalogo: TipoAlertaDef[];
  plantillas: Map<string, PlantillaCorreoEntity>;
  preferencias: Map<string, PreferenciaNotificacionEntity>;
  banderas: BanderasAsistencia;
  ahora: Date;
  baseUrl: string;
  cicloId: string;
  dedupExistentes: Set<string>;
  correosNoTransaccionalesHoy: Map<string, number>;
  generarId: (prefijo: string) => string;
  origenEventoSecuencia?: number;
}

export interface Materializado {
  alertas: AlertaEntity[];
  correos: CorreoSalidaEntity[];
}

export function claveDedup(tipo: CodigoAlerta, destinatarioId: string, claveEntidad: string): string {
  return `${tipo}|${destinatarioId}|${claveEntidad}`;
}

const MOTIVO_RECEPCION: Record<string, string> = {
  ESTUDIANTE_MML: 'tienes una inscripción en una formación de Music Lab',
  MENTOR_MML: 'eres mentor de Music Lab',
  COORDINADOR_MML: 'haces parte de la coordinación de Music Lab'
};

export function rutaPreferencias(d: { tipo: 'USUARIO' | 'PARTICIPANTE' }): string {
  return d.tipo === 'PARTICIPANTE' ? '/mi-progreso-mml/preferencias' : '/admin-asistencia-mml/preferencias';
}

/** Momento de envío respetando el horario silencioso (A3). */
export function programarEnvio(def: TipoAlertaDef, ahora: Date, banderas: BanderasAsistencia): { estado: 'PENDIENTE' | 'PROGRAMADO'; programadoPara: Date } {
  if (def.respetaHorarioSilencioso && def.severidad !== 'CRITICA' && enHorarioSilencioso(ahora, banderas.horarioSilenciosoDesde, banderas.horarioSilenciosoHasta)) {
    return { estado: 'PROGRAMADO', programadoPara: proximaHora(ahora, banderas.horarioSilenciosoHasta) };
  }
  return { estado: 'PENDIENTE', programadoPara: ahora };
}

export function materializarAlertas(borradores: AlertaBorrador[], ctx: ContextoMaterializacion): Materializado {
  const alertas: AlertaEntity[] = [];
  const correos: CorreoSalidaEntity[] = [];
  const nowIso = ctx.ahora.toISOString();

  for (const br of borradores) {
    if (!ctx.banderas.alertasHabilitadas) break;
    const def = definicionAlerta(ctx.catalogo, br.tipo);
    if (!def.activa) continue;
    const dedupKey = claveDedup(br.tipo, br.destinatario.id, br.claveEntidad);
    if (ctx.dedupExistentes.has(dedupKey)) continue;
    ctx.dedupExistentes.add(dedupKey);

    const pref = def.obligatoria ? undefined : ctx.preferencias.get(`${br.destinatario.id}|${br.tipo}`);
    const plantilla = ctx.plantillas.get(def.plantillaId);
    const render = plantilla
      ? renderizarPlantilla(plantilla, br.vars, { baseUrl: ctx.baseUrl, ruta: br.ruta, rutaPreferencias: rutaPreferencias(br.destinatario), obligatoria: def.obligatoria, motivoRecepcion: MOTIVO_RECEPCION[br.destinatario.rol] })
      : undefined;

    const alerta: AlertaEntity = {
      id: ctx.generarId('alr'),
      tipo: br.tipo,
      cicloId: ctx.cicloId,
      destinatarioId: br.destinatario.id,
      destinatarioTipo: br.destinatario.tipo,
      destinatarioRol: br.destinatario.rol,
      destinatarioNombre: br.destinatario.nombre,
      destinatarioCorreo: br.destinatario.correo,
      severidad: def.severidad,
      titulo: render?.asunto ?? def.nombre,
      mensaje: render?.mensajePanel ?? def.descripcion,
      ruta: br.ruta,
      contexto: br.contexto,
      dedupKey,
      estado: 'NUEVA',
      visiblePanel: def.canalesPorDefecto.includes('PANEL') && (pref?.panel ?? true),
      canalCorreo: 'NINGUNO',
      correoIds: [],
      creadaEn: nowIso,
      origenEventoSecuencia: ctx.origenEventoSecuencia
    };

    const quiereCorreo = def.canalesPorDefecto.includes('CORREO') && ctx.banderas.correoHabilitado;
    if (quiereCorreo) {
      const frecuencia = def.obligatoria ? 'INMEDIATO' : pref?.frecuencia ?? (def.correoEnResumenPorDefecto ? 'RESUMEN_DIARIO' : 'INMEDIATO');
      let motivoSupresion: string | undefined;
      if (pref && !pref.correo) motivoSupresion = 'Preferencia del usuario: correo desactivado para este tipo de alerta';
      else if (!plantilla || !render) motivoSupresion = 'NOT-002: plantilla inexistente o inactiva';
      else if (render.faltantes.length) motivoSupresion = `NOT-002: variables faltantes (${render.faltantes.join(', ')})`;

      if (!motivoSupresion && frecuencia !== 'INMEDIATO') {
        alerta.canalCorreo = frecuencia;
      } else {
        let canal: AlertaEntity['canalCorreo'] = 'INMEDIATO';
        if (!motivoSupresion && br.destinatario.tipo === 'PARTICIPANTE' && !def.transaccional) {
          const usados = ctx.correosNoTransaccionalesHoy.get(br.destinatario.id) ?? 0;
          if (usados >= ctx.banderas.maxCorreosNoTransaccionalesDiaEstudiante) canal = 'RESUMEN_DIARIO';
          else ctx.correosNoTransaccionalesHoy.set(br.destinatario.id, usados + 1);
        }
        alerta.canalCorreo = canal;
        if (canal === 'INMEDIATO') {
          const destinos = [br.destinatario.correo ?? '', ...(br.correosAdicionales ?? [])].filter((x, i, arr) => arr.indexOf(x) === i);
          for (const para of destinos) {
            const envio = programarEnvio(def, ctx.ahora, ctx.banderas);
            const invalido = !correoValido(para);
            const motivo = motivoSupresion ?? (invalido ? 'Correo del destinatario ausente o inválido' : undefined);
            const correo: CorreoSalidaEntity = {
              id: ctx.generarId('mail'),
              cicloId: ctx.cicloId,
              alertaIds: [alerta.id],
              destinatarioId: br.destinatario.id,
              para: para || '(sin correo)',
              asunto: render?.asunto ?? def.nombre,
              html: render?.html ?? '',
              texto: render?.texto ?? '',
              plantillaId: def.plantillaId,
              plantillaVersion: plantilla?.version ?? 0,
              tipo: 'INDIVIDUAL',
              critico: def.severidad === 'CRITICA',
              transaccional: def.transaccional,
              estado: motivo ? 'SUPRIMIDO' : envio.estado,
              programadoPara: envio.programadoPara.toISOString(),
              intentos: 0,
              motivoSupresion: motivo,
              idempotencyKey: `${dedupKey}|${para}`,
              creadoEn: nowIso,
              historial: [{ en: nowIso, estado: motivo ? 'SUPRIMIDO' : envio.estado, detalle: motivo ?? (envio.estado === 'PROGRAMADO' ? 'Horario silencioso: se enviará a las ' + ctx.banderas.horarioSilenciosoHasta : 'Encolado') }]
            };
            correos.push(correo);
            alerta.correoIds.push(correo.id);
          }
        }
      }
    }
    alertas.push(alerta);
  }
  return { alertas, correos };
}

/** Resumen consolidado (diario o semanal) por destinatario (HU-34). */
export function construirResumenes(
  pendientes: AlertaEntity[],
  tipo: 'RESUMEN_DIARIO' | 'RESUMEN_SEMANAL',
  ctx: Pick<ContextoMaterializacion, 'plantillas' | 'ahora' | 'baseUrl' | 'cicloId' | 'generarId'>
): { correos: CorreoSalidaEntity[]; alertas: AlertaEntity[] } {
  const plantilla = ctx.plantillas.get('RESUMEN');
  const porDestino = new Map<string, AlertaEntity[]>();
  for (const a of pendientes.filter(x => x.canalCorreo === tipo && x.correoIds.length === 0)) {
    porDestino.set(a.destinatarioId, [...(porDestino.get(a.destinatarioId) ?? []), a]);
  }
  const correos: CorreoSalidaEntity[] = [];
  const alertas: AlertaEntity[] = [];
  const nowIso = ctx.ahora.toISOString();
  const fecha = fechaBogota(ctx.ahora);
  porDestino.forEach((lista, destinatarioId) => {
    const primera = lista[0];
    if (!plantilla || !primera.destinatarioCorreo) return;
    const destino = { tipo: primera.destinatarioTipo };
    const ruta = primera.destinatarioTipo === 'PARTICIPANTE' ? '/mi-progreso-mml/alertas' : '/admin-asistencia-mml/alertas';
    const render = renderizarPlantilla(plantilla, {
      nombre: primera.destinatarioNombre.split(/\s+/)[0],
      cantidad: String(lista.length),
      lista: lista.map(a => `• ${a.titulo}`).join('\n')
    }, { baseUrl: ctx.baseUrl, ruta, rutaPreferencias: rutaPreferencias(destino), obligatoria: false, motivoRecepcion: MOTIVO_RECEPCION[primera.destinatarioRol] });
    const id = ctx.generarId('mail');
    correos.push({
      id, cicloId: ctx.cicloId, alertaIds: lista.map(a => a.id), destinatarioId, para: primera.destinatarioCorreo,
      asunto: render.asunto, html: render.html, texto: render.texto, plantillaId: 'RESUMEN', plantillaVersion: plantilla.version,
      tipo, critico: false, transaccional: false, estado: 'PENDIENTE', programadoPara: nowIso, intentos: 0,
      idempotencyKey: `${tipo}|${destinatarioId}|${fecha}`, creadoEn: nowIso,
      historial: [{ en: nowIso, estado: 'PENDIENTE', detalle: `Resumen con ${lista.length} alerta(s)` }]
    });
    for (const a of lista) alertas.push({ ...a, correoIds: [...a.correoIds, id] });
  });
  return { correos, alertas };
}

/** Plan de reintentos (RNF-04): 1, 5 y 15 minutos; después, FALLIDO. */
export const REINTENTOS_MIN = [1, 5, 15];

export function aplicarResultadoEnvio(correo: CorreoSalidaEntity, ok: boolean, ahora: Date, error?: string): CorreoSalidaEntity {
  const en = ahora.toISOString();
  const intentos = correo.intentos + 1;
  if (ok) {
    return { ...correo, intentos, estado: 'ENVIADO', enviadoEn: en, proximoIntentoEn: undefined, error: undefined, historial: [...correo.historial, { en, estado: 'ENVIADO', detalle: `Intento ${intentos}` }] };
  }
  const espera = REINTENTOS_MIN[intentos - 1];
  if (espera === undefined) {
    return { ...correo, intentos, estado: 'FALLIDO', error, proximoIntentoEn: undefined, historial: [...correo.historial, { en, estado: 'FALLIDO', detalle: `Intento ${intentos}: ${error ?? 'error'}. Sin más reintentos.` }] };
  }
  const proximo = new Date(ahora.getTime() + espera * 60_000).toISOString();
  return { ...correo, intentos, estado: 'PENDIENTE', error, proximoIntentoEn: proximo, historial: [...correo.historial, { en, estado: 'REINTENTO', detalle: `Intento ${intentos} falló (${error ?? 'error'}); reintento en ${espera} min (NOT-003)` }] };
}

export function correoListoParaEnvio(c: CorreoSalidaEntity, ahora: Date): boolean {
  if (c.estado !== 'PENDIENTE' && c.estado !== 'PROGRAMADO') return false;
  const t = ahora.getTime();
  if (new Date(c.programadoPara).getTime() > t) return false;
  return !c.proximoIntentoEn || new Date(c.proximoIntentoEn).getTime() <= t;
}

export function ventanaCierreTexto(s: SesionEntity, banderas: BanderasAsistencia): string {
  return formatoInstante(ventanaToma(s, banderas).cierra.toISOString());
}
